import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import posthog from 'posthog-js';
import { SovrynnLogo } from '../components/Brand';
import PublicLegalFooter from '../components/legal/PublicLegalFooter';
import PassportIdentityBadge from '../components/Passport/PassportIdentityBadge';
import API_URL from '../config';
import { preparePassportTimeline } from '../utils/passportActivityLabel';
import './passport-page.css';

function formatExpiry(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

export default function PassportPage() {
  const { passportId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      setGone(false);
      try {
        const res = await axios.get(
          `${API_URL}/api/passport/view/${encodeURIComponent(passportId)}`,
        );
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 410) setGone(true);
        else if (status === 404) setError('notfound');
        else setError('unknown');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (passportId) load();
    return () => {
      cancelled = true;
    };
  }, [passportId]);

  useEffect(() => {
    if (data && passportId) {
      posthog.capture('compliance_passport_viewed', {
        passportId: passportId,
      });
    }
  }, [data, passportId]);

  if (loading) {
    return (
      <div className="passport-public passport-public--loading">
        <div className="passport-public__inner">
          <SovrynnLogo size="lg" variant="on-light" className="passport-public__logo" />
          <p className="passport-public__loading-text">Loading Compliance Passport™…</p>
          <PublicLegalFooter />
        </div>
      </div>
    );
  }

  if (gone) {
    return (
      <div className="passport-public passport-public--error">
        <div className="passport-public__inner">
          <SovrynnLogo size="lg" variant="on-light" className="passport-public__logo" />
          <h1 className="passport-public__err-title">Compliance Passport™</h1>
          <p className="passport-public__err-body">
            This Compliance Passport™ has expired or been deactivated by the driver.
          </p>
          <a href="https://sovrynn.co" className="passport-public__link">
            sovrynn.co
          </a>
          <PublicLegalFooter />
        </div>
      </div>
    );
  }

  if (error === 'notfound' || error === 'unknown') {
    return (
      <div className="passport-public passport-public--error">
        <div className="passport-public__inner">
          <SovrynnLogo size="lg" variant="on-light" className="passport-public__logo" />
          <h1 className="passport-public__err-title">Compliance Passport™</h1>
          <p className="passport-public__err-body">
            {error === 'notfound' ?
              'This Compliance Passport™ does not exist.'
            : 'Unable to load this passport. Please try again later.'}
          </p>
          <a href="https://sovrynn.co" className="passport-public__link">
            sovrynn.co
          </a>
          <PublicLegalFooter />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const showWatermark = !data.isPremium;
  const passport = data.passport || {};
  const dri = data.dri || {};
  const documents = data.documents || {};
  const authority = data.authority || {};
  const vehicles = data.vehicles || {};
  const timeline = data.timeline || [];
  const verifySlug = passport.passportId || passportId;
  const publicTimeline = preparePassportTimeline(timeline);

  return (
    <div
      className={
        showWatermark ? 'passport-public passport-public--free' : 'passport-public'
      }
    >
      <div className="passport-public__inner">
        {showWatermark && (
          <div className="passport-public__watermark" role="note">
            Generated on Sovrynn Free Plan — Upgrade to Owner-Operator to remove this watermark
          </div>
        )}

        <header className="passport-public__header">
          <SovrynnLogo size="lg" variant="on-light" className="passport-public__logo" />
          <h1 className="passport-public__title">Compliance Passport™</h1>
          <p className="passport-public__subtitle">Verified Living Compliance Record</p>
          <PassportIdentityBadge identityVerified={data.identityVerified} />
        </header>

        <section className="passport-public__card passport-public__id-card" aria-label="Passport identification">
          <div className="passport-public__id-label">PASSPORT ID</div>
          <div className="passport-public__id-value">{passport.passportId}</div>
          <div className="passport-public__id-meta">
            Generated {formatExpiry(passport.createdAt)}
          </div>
          <div className="passport-public__id-meta">
            Expires {formatExpiry(passport.expiresAt)}
          </div>
          <div className="passport-public__id-verify">
            Verify at sovrynn.co/passport/{verifySlug}
          </div>
        </section>

        <section className="passport-public__section" aria-label="DRI Score">
          <h2 className="passport-public__section-title">DRI Score™</h2>
          <div className="passport-public__dri-row">
            <div className="passport-public__dri-score">
              {dri.score}
              <span className="passport-public__dri-max">/100</span>
            </div>
            <div className="passport-public__badges">
              <span
                className="passport-public__pill"
                style={{ backgroundColor: dri.statusColor || '#6b7280' }}
              >
                {dri.status}
              </span>
              <span
                className="passport-public__pill passport-public__pill--outline"
                style={{ borderColor: dri.trendColor || '#6b7280', color: dri.trendColor }}
              >
                {dri.trendLabel}
              </span>
            </div>
          </div>
        </section>

        <section className="passport-public__section" aria-label="Compliance standing">
          <h2 className="passport-public__section-title">Compliance standing</h2>
          <div className="passport-public__bar-wrap">
            <div className="passport-public__bar-bg">
              <div
                className="passport-public__bar-fill"
                style={{ width: `${Math.min(100, documents.percent || 0)}%` }}
              />
            </div>
            <p className="passport-public__bar-caption">
              {documents.completeCount} of {documents.total} required documents complete
            </p>
          </div>
          <ul className="passport-public__doc-list">
            {(documents.items || []).map((item) => (
              <li key={item.name} className="passport-public__doc-row">
                <span className="passport-public__doc-name">{item.name}</span>
                <span
                  className="passport-public__doc-status"
                  style={
                    data.isPremium ? { color: item.color } : undefined
                  }
                >
                  {item.status}
                </span>
              </li>
            ))}
          </ul>
          <p
            className="passport-public__clearinghouse"
            style={{
              color: data.clearinghouseRegistered ? '#16A34A' : '#DC2626',
            }}
          >
            {data.clearinghouseRegistered ?
              'FMCSA Clearinghouse: Registered'
            : 'FMCSA Clearinghouse: Not Registered'}
          </p>
          <div className="passport-public__audit-bar">
            <div className="passport-public__bar-bg">
              <div
                className="passport-public__bar-fill passport-public__bar-fill--amber"
                style={{ width: `${Math.min(100, data.auditReadinessPercent || 0)}%` }}
              />
            </div>
            <p className="passport-public__bar-caption">
              Audit readiness {Math.round(data.auditReadinessPercent || 0)}%
            </p>
          </div>
        </section>

        <section className="passport-public__section" aria-label="Authority">
          <h2 className="passport-public__section-title">Authority</h2>
          <ul className="passport-public__kv">
            <li>
              <span className="passport-public__kv-label">DOT</span>
              <span className="passport-public__kv-value">{authority.dotNumber}</span>
            </li>
            <li>
              <span className="passport-public__kv-label">MC</span>
              <span className="passport-public__kv-value">{authority.mcNumber}</span>
            </li>
            <li>
              <span className="passport-public__kv-label">Operating status</span>
              <span
                className={
                  authority.operatingOk ?
                    'passport-public__kv-value passport-public__kv-value--status-ok'
                  : 'passport-public__kv-value passport-public__kv-value--status-bad'
                }
              >
                {authority.operatingStatus}
              </span>
            </li>
            <li>
              <span className="passport-public__kv-label">MCS-150</span>
              <span className="passport-public__kv-value">{authority.mcs150Status}</span>
            </li>
            <li>
              <span className="passport-public__kv-label">Years active</span>
              <span className="passport-public__kv-value">{authority.yearsActive}</span>
            </li>
          </ul>
        </section>

        <section className="passport-public__section" aria-label="Vehicles">
          <h2 className="passport-public__section-title">Fleet</h2>
          <p className="passport-public__fleet-summary">
            {vehicles.count} active vehicle{vehicles.count === 1 ? '' : 's'} · Fleet Readiness™{' '}
            {vehicles.fleetReadinessScore}/100
          </p>
          <ul className="passport-public__vehicle-list">
            {(vehicles.items || []).map((v) => (
              <li key={v.name}>
                {v.name} — {v.score}/100 — {v.status}
              </li>
            ))}
          </ul>
        </section>

        <section className="passport-public__section" aria-label="History">
          <h2 className="passport-public__section-title">Compliance activity</h2>
          {publicTimeline.length === 0 ?
            <p className="passport-public__muted">No compliance activity recorded yet.</p>
          : <ul className="passport-public__timeline">
              {publicTimeline.map((row, i) => (
                <li key={`${row.date}-${row.description}-${i}`}>
                  <span className="passport-public__tl-date">{row.date}</span>
                  <span className="passport-public__tl-desc">{row.description}</span>
                </li>
              ))}
            </ul>
          }
        </section>

        <footer className="passport-public__footer">
          <p className="passport-public__verify">
            This Compliance Passport™ reflects the carrier&apos;s compliance standing at generation.
            Passport ID: {passport.passportId}.
          </p>
          <p>
            <a href="https://sovrynn.co" className="passport-public__link">
              Powered by Sovrynn — sovrynn.co
            </a>
          </p>
          <a
            className="passport-public__download"
            href={`${API_URL}/api/passport/download/${encodeURIComponent(passport.passportId || passportId)}`}
          >
            Download Passport PDF
          </a>
        </footer>
        <PublicLegalFooter />
      </div>
    </div>
  );
}
